import * as React from "react";

import { cva, type VariantProps } from "@/components/libs/cva";
import { type Merge } from "type-fest";

const { makeProps, variants: typographyVariants } = cva("leading-relaxed", {
  variants: {
    variant: {
      h1: "leading-medium text-h1",
      h2: "font-dots leading-medium text-h2",
      h3: "leading-medium text-h3",
      body: "text-md",
      bodyLg: "text-lg",
      caption: "font-oceanic text-xs",
      button: "font-oceanic text-sm",
    },
    font: {
      dots: "font-dots",
      oceanic: "font-oceanic",
      signifier: "font-signifier",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    color: {
      secondary: "text-text-secondary",
      white: "text-white",
    },
    italic: {
      true: "italic",
    },
    bold: {
      true: "font-bold",
    },
    block: {
      true: "block",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

type TypographyPropsInternal = Merge<
  React.BaseHTMLAttributes<HTMLHeadingElement | HTMLDivElement>,
  VariantProps<typeof typographyVariants> & {
    block?: boolean;
    href?: string;
  }
>;

const Typography = React.forwardRef<
  HTMLHeadingElement | HTMLDivElement,
  TypographyPropsInternal
>((props, ref) => {
  const Comp = React.useMemo(() => {
    if (["h1", "h2", "h3"].includes(props.variant ?? "")) {
      return props.variant;
    }

    if (props.href) {
      return "a";
    }

    return props.block ? "div" : "span";
  }, [props.variant, props.href, props.block]);

  const madeProps = makeProps(props);

  // @ts-expect-error -- ref is ok
  return <Comp ref={ref} {...madeProps} />;
});
Typography.displayName = "Typography";

export type TypographyProps = React.ComponentProps<typeof Typography>;

export { Typography, typographyVariants };
